<template>
  <section
    class="cart-upsell"
    :class="{ [$class('loading')]: state === 'loading' }"
    aria-labelledby="cart-upsell-title"
  >
    <span
      id="cart-upsell-title"
      class="cart-upsell__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      v-text="$string('cart.samples.title')"
    />
    <div
      v-if="filteredProducts.length"
      ref="carousel"
      class="cart-upsell__splide splide"
    >
      <div class="splide__track carousel">
        <div class="cart-upsell__list carousel splide__list">
          <div
            v-for="(product, index) of filteredProducts"
            :key="getProductKey(product, index)"
            class="splide__slide"
          >
            <product-card-sample
              class="cart-upsell__product"
              :product="product"
              :variant="product.selectedOrFirstAvailableVariant"
              :disabled="getCartItemLimitReached"
              @emit-error="handleError"
            />
          </div>
        </div>
      </div>

      <div class="splide__arrows">
        <button class="splide__arrow splide__arrow--prev" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button class="splide__arrow splide__arrow--next" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>
    </div>

    <div v-else>
      <p
        class="cart-upsell__empty text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="$string('cart.samples.empty')"
      />
    </div>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </section>
</template>

<script>
/**
 * Vue: Cart upsell (cart-upsell)
 * -----------------------------------------------------------------------------
 * Shows a set of upsell products selected from the customizer in the drawer cart.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Array} samples - Array of sample products to load.
 *
 */
import { Splide } from '@splidejs/splide';
import { markRaw } from 'vue';
import { mapState } from 'pinia';

import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import ProductCardSample from '~async/product-card-sample/product-card-sample';

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';

import nodesByIdQuery from '~graphql/nodes-by-id-product-inline.gql';
import { formatProduct } from '~/format/format-product';
import { transformId } from '~/helpers/graphql';
import timings from '~/config/timings';
import useCartStore from '~stores/cart';
import useIndexStore from '~stores/index';

export default {
  name: 'CartUpsell',

  components: {
    NotificationPanel,
    ProductCardSample,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    samples: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      carousel: null,
      carouselMountTimeout: null,
      error: '',
      filteredProducts: [false, false, false],
      previousCartItemCount: 0,
      products: [],
      state: 'loading',
      timeout: {},
    };
  },

  computed: {
    ...mapState(useCartStore, {
      getCartItemLimitReached: 'getCartItemLimitReached',
      cart: 'response',
    }),

    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
  },

  watch: {
    /**
     * Watch incoming sample list.
     * Needed when AB-test assignment resolves after component mount.
     */
    samples(newSamples, oldSamples) {
      if (newSamples === oldSamples) {
        return;
      }

      this.fetchData();
    },

    /**
     * Watch cart items to update filtered products.
     */
    'cart.items'() {
      let timing = this.$timing('addedToCart');

      /**
       * If less items in cart than previously then instantly update.
       */
      if (this.cart.item_count < this.previousCartItemCount) {
        timing = 0;
      }

      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        this.filterProducts();
        this.previousCartItemCount = this.cart.item_count;
        this.refreshCarousel();
      }, timing);
    },

    async isMobile() {
      await this.$nextTick();
      this.handleResize();
    },
  },

  mounted() {
    this.fetchData();
  },

  beforeUnmount() {
    this.destroyCarousel();
  },

  methods: {
    /**
     * Build a stable unique key for cart sample slides.
     * Duplicate products/variants can appear in metaobject lists.
     * @param {Object} product - Product object.
     * @param {Number} index - Iteration index.
     * @returns {String}
     */
    getProductKey(product, index) {
      const handle = product?.handle || 'unknown';
      const variantId =
        product?.selectedOrFirstAvailableVariant?.id || 'no-variant';

      return `${handle}-${variantId}-${index}`;
    },

    /**
     * Fully tear down the carousel.
     * Clears any pending mount first so a queued mount can't run against a
     * destroyed instance, which would re-initialise Splide on mobile and
     * break the native scroll fallback. Always reset the reference so the
     * next init starts from the same clean state as the very first one.
     */
    destroyCarousel() {
      if (this.carouselMountTimeout) {
        clearTimeout(this.carouselMountTimeout);
        this.carouselMountTimeout = null;
      }

      if (this.carousel) {
        this.carousel.destroy();
        this.carousel = null;
      }
    },

    /**
     * Construct the carousel.
     */
    constructCarousel() {
      if (!this.$refs.carousel) {
        return;
      }

      this.destroyCarousel();

      const splide = new Splide(this.$refs.carousel, {
        arrows: !this.isMobile,
        drag: false,
        fixedWidth: '11rem',
        focus: false,
        gap: 'var(--spacing-xs)',
        keyboard: true,
        paginationKeyboard: true,
        mediaQuery: 'min',
        omitEnd: true,
        padding: { right: 'var(--spacing-l)' },
        pagination: false,
        perMove: 1,
        slideFocus: false,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
      });

      /**
       * Store the raw instance. Splide must not become a reactive Vue proxy:
       * a proxy would never be `===` the local `splide` reference below, so
       * the mount guard would always bail and the carousel would never mount.
       */
      this.carousel = markRaw(splide);

      this.carouselMountTimeout = setTimeout(() => {
        if (this.carousel !== splide) {
          return;
        }

        splide.mount();
        this.carouselMountTimeout = null;
      }, timings.normal);
    },

    /**
     * Refresh carousel. Will be triggered when filtered products change
     */
    async refreshCarousel() {
      if (this.carousel) {
        await this.$nextTick();
        this.carousel.refresh();
      }
    },

    /**
     * Fetch data.
     */
    async fetchData() {
      try {
        const ids = this.samples.map(({ id }) => transformId(id, 'Product'));

        if (!ids) {
          return;
        }

        const response = await cnvs.Query({
          query: nodesByIdQuery,
          variables: {
            country: this.$variable('store.country'),
            ids,
            language: this.$variable('store.language'),
          },
        });

        if (this.$variable('settings.disableComponentReadyState')) {
          return;
        }

        this.products = response.nodes
          .map((product, index) => {
            if (!product) {
              return false;
            }

            let variantId = false;

            if (this.samples[index]) {
              variantId = this.samples[index].variantId;
            }

            return formatProduct(product, { variantId });
          })
          .filter(Boolean);

        this.state = 'ready';
        this.filterProducts();

        if (this.isMobile) {
          return;
        }

        await this.$nextTick();

        /**
         * Build the carousel once on first load. When the sample list later
         * changes (only happens for the ESN-53 AB-test), keep the same
         * instance and its settings/state and just refresh it so Splide
         * re-reads the new slides instead of destroying and rebuilding.
         */
        if (this.carousel) {
          this.carousel.refresh();
        } else {
          this.constructCarousel();
        }
      } catch (error) {
        this.state = 'error';
        cnvs.ReportError('Failed to load cart upsell', error);
      }
    },

    /**
     * Filter out variants already in cart or unavailable.
     */
    filterProducts() {
      if (this.state === 'loading') {
        this.filteredProducts = [false, false, false];
        return;
      }

      this.filteredProducts = this.products.filter((product) => {
        const variant = product.selectedOrFirstAvailableVariant;

        const variantInCart = this.cart.items.find((item) => {
          return item.variant_id === variant?.id;
        });

        return (
          variant?.available &&
          variant?.availableState !== 'hidden' &&
          !variantInCart
        );
      });

      this.filteredProducts = this.filteredProducts.slice(0, 8);

      this.overlay.refresh({ currentElement: document.activeElement });
    },

    /**
     * Handle resize event.
     */
    handleResize() {
      if (!this.isMobile) {
        this.constructCarousel();

        return;
      }

      this.destroyCarousel();
    },

    /**
     * Handle error from cart sample.
     * @param {String} error - Error message.
     */
    handleError(error) {
      this.error = error;
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './cart-upsell';
</style>
